import { useEffect, useState } from 'react';
import { Activity, Coins, Package, Users } from 'lucide-react';
import {
  FAMILY_LABELS, FAMILY_BASELINES, FAMILY_TO_SECTION_KIND,
  getKpisByFamily, getSection,
} from '../utils';
import KpiBigCard from './KpiBigCard';
import MiniChart12Months from './MiniChart12Months';
import SeniorAnalysis from './SeniorAnalysis';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAMILY_ICONS = {
  activite: Activity,
  rentabilite: Coins,
  stock: Package,
  patientele: Users,
};

/**
 * Onglet de détail famille — composant générique paramétré par `family`.
 * Utilisé pour les 4 onglets (Activité, Rentabilité, Stock, Patientèle).
 *
 * Layout (brief produit) :
 *   1. Header : icône + nom famille (serif 19) + baseline italique
 *   2. Grille 2 colonnes des KPIs de la famille (KpiBigCard)
 *   3. Mini-graphique 12 mois du KPI primary (SVG natif)
 *   4. Bloc analyse senior (3 paragraphes, dégradation gracieuse)
 *
 * Le mini-graphique est chargé en lazy via `/api/client/monthly-reports/series/:period/:family`
 * pour éviter de gonfler le payload du rapport principal.
 */
export default function FamilyDetailTab({ family, report }) {
  const Icon = FAMILY_ICONS[family];
  const label = FAMILY_LABELS[family] || family;
  const baseline = FAMILY_BASELINES[family] || '';
  const kpis = getKpisByFamily(report, family);
  const section = getSection(report, FAMILY_TO_SECTION_KIND[family]);

  const [series, setSeries] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('benchory_token');
    if (!token || !report?.period) {
      setSeriesLoading(false);
      return;
    }
    setSeriesLoading(true);
    fetch(`${API_URL}/client/monthly-reports/series/${report.period}/${family}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) {
          setSeries(data || null);
          setSeriesLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setSeriesLoading(false);
      });
    return () => { cancelled = true; };
  }, [family, report?.period]);

  return (
    <section className="space-y-10" data-testid={`family-tab-${family}`}>
      <div>
        <div className="text-[10px] text-cabinet-bordeaux uppercase tracking-[0.3em] font-medium mb-2">
          IV — Détail par famille
        </div>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 bg-cabinet-cream-dark border border-cabinet-border flex items-center justify-center shrink-0">
            {Icon && <Icon size={20} className="text-cabinet-bordeaux" strokeWidth={1.6} />}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-playfair text-[22px] text-cabinet-ink leading-tight">{label}</h2>
            <p className="font-playfair italic text-[14px] text-cabinet-ink-secondary mt-1 max-w-2xl">
              {baseline}
            </p>
          </div>
        </div>
      </div>

      {kpis.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {kpis.map((kpi) => <KpiBigCard key={kpi.kpi_id} kpi={kpi} />)}
        </div>
      ) : (
        <div className="bg-cabinet-paper border border-cabinet-border p-8 text-center">
          <p className="font-playfair italic text-[13px] text-cabinet-ink-soft">
            Aucun indicateur disponible pour cette famille dans le rapport courant.
          </p>
        </div>
      )}

      {seriesLoading ? (
        <div className="bg-cabinet-paper border border-cabinet-border p-6 text-center">
          <div className="w-6 h-6 border-2 border-cabinet-border border-t-cabinet-bordeaux rounded-full animate-spin mx-auto" />
        </div>
      ) : (series?.primary_kpi) ? (
        <MiniChart12Months
          series={series.series || []}
          benchmarkMedian={series.benchmark_median}
          kpi={series.primary_kpi}
        />
      ) : (
        <div className="bg-cabinet-paper border border-cabinet-border p-6">
          <p className="font-playfair italic text-[12px] text-cabinet-ink-soft">
            Le mini-graphique d'évolution sera disponible dès la deuxième saisie mensuelle.
          </p>
        </div>
      )}

      <SeniorAnalysis section={section} />
    </section>
  );
}
